<template>
  <Dialog
    :model-value="visible"
    title="Date Override"
    dialog-type="primary"
    dialog-size="small"
    hideHeaderIcon
    :close-on-overlay="!isApplying"
    @update:model-value="(value) => { if (!value) handleClose() }"
    @close="handleClose"
  >
    <Loading :loading="isApplying" :loading-text="loadingText">
      <div class="flex flex-col items-center p-3 gap-6">
        <div class="dialog-title">Select the date(s) you want to assign specific hours</div>
        <VCalendar
          ref="calendar"
          data-testid="my-calendar.override-date-dialog.mini-calendar"
          title-position="right"
          :attributes="calendarAttrs"
          color="vibrant-blue"
          class="mini-calendar"
          @dayclick="handleDayClick"
        >
          <template #header-prev-button >
            <img data-testid="my-calendar.override-date-dialog.prev-month-icon" class="w-3 h-3" src="@/assets/my-calendar/calendar/calendar-left-arrow.svg"  alt="prevIcon" />
          </template>
          <template #header-next-button>
            <img data-testid="my-calendar.override-date-dialog.next-month-icon" class="w-3 h-3 rotate-180" src="@/assets/my-calendar/calendar/calendar-left-arrow.svg" alt="nextIcon" />
          </template>
        </VCalendar>
      </div>

      <div class="override-section">
        <div class="override-title">Available hours for booking (override):</div>

        <div data-testid="my-calendar.override-date-dialog.unavailable-option" class="override-option" @click="selectedMode = 'unavailable'">
          <div class="radio" :class="{ selected: selectedMode === 'unavailable' }"></div>
          <div class="override-option-label">Unavailable all day</div>
        </div>

        <div data-testid="my-calendar.override-date-dialog.custom-hours-option" class="override-option" @click="selectedMode = 'custom'">
          <div class="radio" :class="{ selected: selectedMode === 'custom' }"></div>
          <div class="override-option-label">Custom hours</div>
        </div>

        <div v-if="selectedMode === 'custom'" class="custom-hours">
          <div v-if="selectedDates.length > 0">
            <div
              v-for="(slot, idx) in slots"
              :key="idx"
              :data-testid="`my-calendar.override-date-dialog.slot-row.${idx}`"
              class="flex items-center gap-3 mb-1"
            >
              <div class="w-[120px]">
                <SingleSelector
                  :data-testid="`my-calendar.override-date-dialog.start-time-select.${idx}`"
                  class="time-select"
                  placeholder="Start"
                  :options="timeOptions"
                  size="medium"
                  :model-value="slot.start"
                  @update:model-value="val => updateStartTime(idx, val)"
                />
              </div>
              <div class="divider-rect"></div>
              <div class="w-[120px]">
                <SingleSelector
                  :data-testid="`my-calendar.override-date-dialog.end-time-select.${idx}`"
                  class="time-select"
                  placeholder="End"
                  :options="getFilteredEndOptions(slot.start)"
                  size="medium"
                  :model-value="slot.end"
                  @update:model-value="val => updateEndTime(idx, val)"
                />
              </div>
              <div class="flex gap-3">
                <img
                  :data-testid="`my-calendar.override-date-dialog.remove-slot-btn.${idx}`"
                  class="cursor-pointer"
                  src="@/assets/my-calendar/availability/close.svg"
                  alt="delete"
                  @click="handleDelete(idx)"
                />
                <img
                  :data-testid="`my-calendar.override-date-dialog.add-slot-btn.${idx}`"
                  class="cursor-pointer"
                  src="@/assets/my-calendar/availability/add-circle.svg"
                  alt="add"
                  @click="handleAdd"
                />
              </div>
            </div>
          </div>
          <div v-else data-testid="my-calendar.override-date-dialog.no-date-selected-text" class="text-xs text-gray-400 mt-2">Please select at least one date.</div>
          <div class="override-desc">Overrides replace your usual weekly availability for the selected date(s).</div>
        </div>
      </div>
    </Loading>

    <template #footer>
      <div class="flex justify-between items-center w-full">
        <ResponsiveButton
          data-testid="my-calendar.override-date-dialog.cancel-btn"
          button_type="text"
          built_in_theme="dark"
          size="medium"
          display_name="Cancel"
          :disabled="isApplying"
          @click-button="handleClose"
        />
        <ResponsiveButton
          data-testid="my-calendar.override-date-dialog.apply-btn"
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          :display_name="applyButtonText"
          :disabled="!canApply || isApplying"
          @click-button="handleApply"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
// Import dependencies and components
import { ref, computed, watch } from 'vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { getTimeOptions } from '@/views/my-calendar/mock-data'
import moment from 'moment';

// Props: controls dialog visibility from parent
const props = defineProps({
  visible: { type: Boolean, default: false },
  duration: {
    type: [String, Number],
    default: 60,
  },
  editingException: {
    type: Object,
    default: null,
  },
  saveException: {
    type: Function,
    default: null,
  },
});
// Emits: update:date (send selected slots), close (close dialog)
const emit = defineEmits(['addException', 'close']);

// Stores selected dates as 'YYYY-MM-DD' strings
const selectedDates = ref([]);
const timeOptions = computed(() => getTimeOptions(props.duration));
// Stores all time slots, controlled by add/delete (shared for all selected dates)
const slots = ref([{ start: '', end: '' }]);
const selectedMode = ref('unavailable');
const isApplying = ref(false);

const isEditMode = computed(() => !!props.editingException?.exception_id);
const applyButtonText = computed(() => (isEditMode.value ? 'Save' : 'Apply'));
const loadingText = computed(() => (isEditMode.value ? 'Saving date override...' : 'Creating date override...'));

/**
 * Computed: highlights all selected dates in the calendar.
 * Uses T12:00:00 to avoid timezone issues when converting to Date.
 */
const calendarAttrs = computed(() => [
  ...selectedDates.value.map(date => ({
    key: date,
    highlight: {
      color: '#004879',
      fillMode: 'solid',
    },
    dates: new Date(date + 'T12:00:00')
  }))
]);

/**
 * Handles clicking a day in the calendar.
 * Toggles the date in selectedDates; does not affect slot rows.
 */
const handleDayClick = (day) => {
  const dateStr = moment(day.date).format('YYYY-MM-DD');
  if (isEditMode.value) {
    if (selectedDates.value[0] === dateStr) {
      selectedDates.value = [];
    } else {
      selectedDates.value = [dateStr];
    }
    return;
  }
  const idx = selectedDates.value.indexOf(dateStr);
  if (idx >= 0) {
    // Remove date if already selected
    selectedDates.value.splice(idx, 1);
  } else {
    // Add date if not selected
    selectedDates.value.push(dateStr);
  }
};

/**
 * Update the start time of a slot row.
 * If the new start is after or equal to end, clear the end.
 */
function updateStartTime(idx, val) {
  slots.value[idx].start = val;
  if (slots.value[idx].end && val >= slots.value[idx].end) {
    slots.value[idx].end = '';
  }
}
/**
 * Update the end time of a slot row.
 */
function updateEndTime(idx, val) {
  slots.value[idx].end = val;
}
/**
 * Add a new empty slot row.
 */
function handleAdd() {
  slots.value.push({ start: '', end: '' });
}
/**
 * Delete a slot row by index.
 */
function handleDelete(idx) {
  if (slots.value.length === 1) {
    // If only one slot left, just clear it
    slots.value[0] = { start: '', end: '' };
  } else {
    slots.value.splice(idx, 1);
  }
}
/**
 * Get end time options that are after the selected start time.
 */
function getFilteredEndOptions(start) {
  if (!start) return timeOptions.value;
  const startIndex = timeOptions.value.findIndex(opt => opt.value === start);
  return startIndex >= 0 ? timeOptions.value.slice(startIndex + 1) : timeOptions.value;
}

/**
 * Only allow Apply if at least one date is selected and at least one slot is valid.
 */
const canApply = computed(() =>
  selectedDates.value.length > 0 &&
  (
    selectedMode.value === 'unavailable' ||
    slots.value.some(slot => slot.start && slot.end)
  )
);

/**
 * When Apply is clicked, emit all combinations of selected dates and valid slots.
 */
async function handleApply() {
  // For each date, for each slot, emit a schedule exception
  const result = [];
  selectedDates.value.forEach(date => {
    if (selectedMode.value === 'unavailable') {
      result.push({
        date,
        start_time: null,
        end_time: null,
        is_available: false
      });
      return;
    }

    slots.value.forEach(slot => {
      if (slot.start && slot.end) {
        result.push({
          date,
          start_time: moment(slot.start, 'hh:mmA').format('HH:mm:00'),
          end_time: moment(slot.end, 'hh:mmA').format('HH:mm:00'),
          is_available: false
        });
      }
    });
  });
  isApplying.value = true;
  try {
    if (typeof props.saveException === 'function') {
      try {
        await props.saveException(result);
      } catch (e) {
        // Intentionally swallow to ensure dialog closes regardless of failure
      }
      emit('close');
      return;
    }

    // Backward compatible: if parent still listens to event, we can't await it.
    emit('addException', result);
    emit('close');
  } finally {
    isApplying.value = false;
  }
}
/**
 * Close dialog.
 */
function handleClose() {
  if (isApplying.value) return;
  emit('close');
}

watch(
  () => props.editingException,
  (exception) => {
    if (exception?.exception_id) {
      selectedDates.value = exception.date ? [exception.date] : [];
      if (exception.start_time && exception.end_time) {
        selectedMode.value = 'custom';
        slots.value = [{
          start: moment(exception.start_time, 'HH:mm:ss').format('hh:mma'),
          end: moment(exception.end_time, 'HH:mm:ss').format('hh:mma'),
        }];
      } else {
        selectedMode.value = 'unavailable';
        slots.value = [{ start: '', end: '' }];
      }
      return;
    }

    selectedDates.value = [];
    selectedMode.value = 'unavailable';
    slots.value = [{ start: '', end: '' }];
  },
  { immediate: true }
)
</script>

<style scoped>
.dialog-title {
  font-weight: 500;
  font-size: 18px;
  font-family: 'Inter';
  line-height: 24px;
  letter-spacing: 0%;
}

.override-section {
  margin-top: 24px;
  background: #F9FBFD;
  border-top: 1px dashed #C4C4C4;

  padding: 12px 40px;
}

.override-title {
  font-weight: 500;
  font-size: 14px;
  font-family: 'Inter';
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333333;
  margin-bottom: 12px;
}

.override-option {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 24px;
  cursor: pointer;
  margin-bottom: 12px;
}

.override-option:last-of-type {
  margin-bottom: 0;
}

.override-option-label {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333333;
}

.radio {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  border: 1px solid #004879;
  position: relative;
  box-sizing: border-box;
}

.radio.selected::after {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 9999px;
  background: #004879;
}

.custom-hours {
  margin-top: 12px;
}

.override-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.2px;
  color: #999999;
}
.divider-rect {
  width: 7px;
  height: 2px;
  background: #999999;
  align-self: center;
  margin: 0 4px;
}
.time-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border:none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 6px 8px 6px 12px;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder) {
  color: #333;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder.is-transparent) {
  color: #666;
}
</style>